import {Badge, FieldRequiredLabel, InfoTooltip, Typography} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {useTranslation} from 'react-i18next';

import {FileUploadManager} from './FileUploadManager';

import type {DocumentContext, DocumentEntityType, DocumentType} from '@sollapay/enums';
import type {DocumentMetadata} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

export interface DocumentUploadSectionProps {
  title: string;
  titleTooltip?: ReactNode;
  description?: string;
  examples?: string[];
  required?: boolean;
  error?: string;
  maxFiles?: number;
  className?: string;
  entityType: DocumentEntityType;
  entityId: string;
  documentContext: DocumentContext;
  documentType: DocumentType;
  metadata?: DocumentMetadata;
  /** Scopes which existing documents are shown/counted — e.g. `{partyId}` for a per-beneficiary upload. */
  metadataFilter?: Record<string, string | undefined>;
  /** When true, hides the upload dropzone and delete affordance — documents become view/download only. */
  readOnly?: boolean;
  onError?: (error: unknown) => void;
  onDocumentChange?: () => void;
  onDocumentUploaded?: (documentId: string) => void;
  /** Fires whenever the set of already-uploaded documents (re)loads — e.g. to sync form state on mount. */
  onDocumentsLoaded?: (documentIds: string[]) => void;
}

export const DocumentUploadSection: FC<DocumentUploadSectionProps> = ({
  title,
  titleTooltip,
  description,
  examples,
  required,
  error,
  maxFiles,
  className,
  entityType,
  entityId,
  documentContext,
  documentType,
  metadata,
  metadataFilter,
  readOnly,
  onError,
  onDocumentChange,
  onDocumentUploaded,
  onDocumentsLoaded
}) => {
  const {t} = useTranslation('common');

  return (
    <div
      className={cn('flex flex-col gap-4 rounded-md border border-border-subtle p-4', className)}
      data-slot="document-upload-section"
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <Typography size="sm" weight="semibold">
            <FieldRequiredLabel required={required}>{title}</FieldRequiredLabel>
          </Typography>
          {titleTooltip && <InfoTooltip content={titleTooltip} />}
        </div>
        {description && (
          <Typography size="xs" color="secondary">
            {description}
          </Typography>
        )}
        {examples != null && examples.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <Typography size="xs" color="tertiary" weight="semibold">
              {t('examplesLabel')}
            </Typography>
            {examples.map(example => (
              <Badge key={example} size="xs" variant="neutral">
                {example}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <FileUploadManager
        entityType={entityType}
        entityId={entityId}
        documentContext={documentContext}
        documentType={documentType}
        metadata={metadata}
        metadataFilter={metadataFilter}
        maxFiles={maxFiles}
        error={error}
        readOnly={readOnly}
        onError={onError}
        onDocumentChange={onDocumentChange}
        onDocumentUploaded={onDocumentUploaded}
        onDocumentsLoaded={onDocumentsLoaded}
      />
    </div>
  );
};
